import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthGuard, AdminGuard, AdminOnlyGuard } from './components/check/AuthGuard';
import UnifiedChat from './components/check/UnifiedChat';
import FloatingActionBar from './components/check/FloatingActionBar';
import AdminTopBar from './components/admin/AdminTopBar';

// Retry-on-stale-chunk wrapper: if a chunk fails to load (usually after a deploy),
// force a hard reload so the browser fetches the new index.html with fresh chunk hashes.
function lazyRetry<T>(factory: () => Promise<{ default: React.ComponentType<T> }>) {
  return React.lazy(() => factory().catch((err) => {
    const key = 'chunk_reload_attempt';
    if (sessionStorage.getItem(key) !== '1') {
      sessionStorage.setItem(key, '1');
      window.location.reload();
    }
    throw err;
  }));
}

// Clear the reload-attempt flag once any chunk succeeds
window.addEventListener('load', () => sessionStorage.removeItem('chunk_reload_attempt'));

// Lazy-load all pages for code splitting
const LandingPage     = lazyRetry(() => import('./pages/check/LandingPage'));
const LoginPage       = lazyRetry(() => import('./pages/check/LoginPage'));
const PricingPage     = lazyRetry(() => import('./pages/check/PricingPage'));
const BillingPage     = lazyRetry(() => import('./pages/check/BillingPage'));
const ChatAnalysis    = lazyRetry(() => import('./pages/check/ChatAnalysis'));
const SellerPortfolio = lazyRetry(() => import('./pages/check/SellerPortfolio'));
const RatingPage      = lazyRetry(() => import('./pages/check/RatingPage'));
const Marketplace     = lazyRetry(() => import('./pages/check/Marketplace'));
const MarketListingPage = lazyRetry(() => import('./pages/check/MarketListingPage'));
const DealRoomPage    = lazyRetry(() => import('./pages/check/DealRoomPage'));
const DemoBangchakPage = lazyRetry(() => import('./pages/check/DemoBangchakPage'));
const FddCalculatorPage = lazyRetry(() => import('./pages/check/FddCalculatorPage'));
const OwnerBasicCheckPage = lazyRetry(() => import('./pages/check/OwnerBasicCheckPage'));
const StationIntakePage = lazyRetry(() => import('./pages/intake/StationIntakePage'));
const AdminDashboard  = lazyRetry(() => import('./pages/admin/AdminDashboard'));
const StationIntakeAdminPage = lazyRetry(() => import('./pages/admin/StationIntakeAdminPage'));
const ListingReviewPage = lazyRetry(() => import('./pages/admin/ListingReviewPage'));
const AdminUsersPage  = lazyRetry(() => import('./pages/admin/AdminUsersPage'));
const AdminBillingPage = lazyRetry(() => import('./pages/admin/AdminBillingPage'));
const ChangeRequestQueue = lazyRetry(() => import('./pages/admin/ChangeRequestQueue'));
const ApprovalDashboard = lazyRetry(() => import('./pages/check/ApprovalDashboard'));
const OperatorSalesPage = lazyRetry(() => import('./pages/check/OperatorSalesPage'));
const ComparePage     = lazyRetry(() => import('./pages/check/ComparePage'));
const HomePage        = lazyRetry(() => import('./pages/HomePage'));
const ProjectPage     = lazyRetry(() => import('./pages/ProjectPage'));
const ResultPage      = lazyRetry(() => import('./pages/ResultPage'));

const Fallback = () => <div style={{ display:'flex', height:'100vh', alignItems:'center', justifyContent:'center', background:'#0a0e19', color:'#64748b' }}>Loading...</div>;

const WithUnifiedChat: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <FloatingActionBar />
    {children}
    <UnifiedChat hideBubble />
  </>
);

// Admin pages get a centered grouped top bar instead of the floating UserMenu
const WithAdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <AdminTopBar />
    {children}
    <UnifiedChat />
  </>
);

function RaterRoutes() {
  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/demo/bangchak" element={<DemoBangchakPage />} />
        <Route path="/fdd-calculator" element={<FddCalculatorPage />} />
        <Route path="/owner-basic-check" element={<OwnerBasicCheckPage />} />
        <Route path="/data-collection" element={<StationIntakePage />} />
        <Route path="/billing" element={<AuthGuard><WithUnifiedChat><BillingPage /></WithUnifiedChat></AuthGuard>} />
        <Route path="/chat" element={<AuthGuard><Navigate to="/portfolio" replace /></AuthGuard>} />
        <Route path="/chat/:plantId" element={<AuthGuard><FloatingActionBar /><ChatAnalysis /></AuthGuard>} />
        <Route path="/portfolio" element={<AuthGuard><WithUnifiedChat><SellerPortfolio /></WithUnifiedChat></AuthGuard>} />
        <Route path="/marketplace" element={<AuthGuard allowTermsPending><WithUnifiedChat><Marketplace /></WithUnifiedChat></AuthGuard>} />
        <Route path="/marketplace/listings/:id" element={<AuthGuard allowTermsPending><WithUnifiedChat><MarketListingPage /></WithUnifiedChat></AuthGuard>} />
        <Route path="/marketplace/:id" element={<Navigate to="/marketplace" replace />} />
        <Route path="/deals/:id" element={<AuthGuard><WithUnifiedChat><DealRoomPage /></WithUnifiedChat></AuthGuard>} />
        <Route path="/rating/:id" element={<AuthGuard><WithUnifiedChat><RatingPage /></WithUnifiedChat></AuthGuard>} />
        <Route path="/compare" element={<AuthGuard><WithUnifiedChat><ComparePage /></WithUnifiedChat></AuthGuard>} />
        <Route path="/credits" element={<AuthGuard><Navigate to="/billing" replace /></AuthGuard>} />
        <Route path="/admin" element={<AdminGuard><WithAdminLayout><ListingReviewPage /></WithAdminLayout></AdminGuard>} />
        <Route path="/admin/change-requests" element={<AdminGuard><WithAdminLayout><ChangeRequestQueue /></WithAdminLayout></AdminGuard>} />
        <Route path="/admin/plants" element={<AdminGuard><WithAdminLayout><AdminDashboard /></WithAdminLayout></AdminGuard>} />
        <Route path="/admin/station-intake" element={<AdminGuard><WithAdminLayout><StationIntakeAdminPage /></WithAdminLayout></AdminGuard>} />
        <Route path="/admin/users" element={<AdminOnlyGuard><WithAdminLayout><AdminUsersPage /></WithAdminLayout></AdminOnlyGuard>} />
        <Route path="/admin/billing" element={<AdminOnlyGuard><WithAdminLayout><AdminBillingPage /></WithAdminLayout></AdminOnlyGuard>} />
        <Route path="/operator/approvals" element={<AdminGuard><WithUnifiedChat><ApprovalDashboard /></WithUnifiedChat></AdminGuard>} />
        <Route path="/operator/sales" element={<AdminGuard><WithUnifiedChat><OperatorSalesPage /></WithUnifiedChat></AdminGuard>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function SunSimRoutes() {
  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/project/:id" element={<ProjectPage />} />
        <Route path="/results/:id" element={<ResultPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function AppRoutes() {
  const path = window.location.pathname;
  const isRater = !path.startsWith('/pv');
  return isRater ? <RaterRoutes /> : <SunSimRoutes />;
}
